import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import BarChartIcon from '@mui/icons-material/BarChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FunctionsIcon from '@mui/icons-material/Functions';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { CalculatedAttribute, DateRangePreset } from '../../types';
import { toDisplayName, isSimpleAggregateFormula, getDateDimensions, filterTreeNodes, DATE_RANGE_PRESETS, getMetricPrimarySortColumn, getDimensionOutputAlias } from '../../utils';
import { TIME_PERIOD_AGGREGATIONS } from '../../constants';
import { buildDisplayNameMap } from '../../utils/workspaceHelpers';
import { FilterGroupBuilder } from '../FilterBuilder';
import MetricConfigPopover from '../MetricConfigPopover';
import { TreeNodeItem } from '../TreeView';
import CalculatedComponents from '../CalculatedComponents/CalculatedComponents';
import {
  qbBadge, qbBrowser, qbBrowserCollapsed, qbBrowserHeader, qbBrowserTitle, qbCalcChip, qbCard, qbCardBody,
  qbCardHeader, qbCards, qbDimensionChip, qbEmpty, qbIconButton, qbMetricChip,
} from './queryBuilderPresentation';

/** One selection card: a .panel with its .ph header. */
const SelectionCard: React.FC<React.PropsWithChildren<{
  icon: React.ReactNode; title: string; testId: string;
}>> = ({ icon, title, testId, children }) => (
  <Paper elevation={0} sx={qbCard} data-testid={testId} component="section" aria-label={title}>
    <Typography component="h3" sx={qbCardHeader}>{icon}{title}</Typography>
    <Box sx={qbCardBody}>{children}</Box>
  </Paper>
);

const SIMPLE_AGG_EXTRACT = /^(SUM|AVG|COUNT|MIN|MAX|MEDIAN|STDDEV|VARIANCE)\s*\(/i;

const VoyagerPanes: React.FC = () => {
  const treeNodes = useVoyagerStore((s) => s.treeNodes);
  const selectedMetrics = useVoyagerStore((s) => s.selectedMetrics);
  const selectedDimensions = useVoyagerStore((s) => s.selectedDimensions);
  const filterGroup = useVoyagerStore((s) => s.filterGroup);
  const dateRangeStart = useVoyagerStore((s) => s.dateRangeStart);
  const dateRangeEnd = useVoyagerStore((s) => s.dateRangeEnd);
  const setDateRange = useVoyagerStore((s) => s.setDateRange);
  const dateRangePreset = useVoyagerStore((s) => s.dateRangePreset);
  const setDateRangePreset = useVoyagerStore((s) => s.setDateRangePreset);
  const toggleDimension = useVoyagerStore((s) => s.toggleDimension);
  const removeMetricInstance = useVoyagerStore((s) => s.removeMetricInstance);
  const semanticModel = useVoyagerStore((s) => s.semanticModel);
  const metricConfigs = useVoyagerStore((s) => s.metricConfigs);
  const calculatedAttributes = useVoyagerStore((s) => s.calculatedAttributes);
  const removeCalculatedField = useVoyagerStore((s) => s.removeCalculatedField);
  const orderBy = useVoyagerStore((s) => s.orderBy);
  const cycleOrderBy = useVoyagerStore((s) => s.cycleOrderBy);
  const removeOrderBy = useVoyagerStore((s) => s.removeOrderBy);
  const isModelBrowserOpen = useVoyagerStore((s) => s.isModelBrowserOpen);
  const toggleModelBrowser = useVoyagerStore((s) => s.toggleModelBrowser);
  const [browserSearch, setBrowserSearch] = React.useState('');
  const [calcDialogOpen, setCalcDialogOpen] = React.useState(false);
  const [editingCalcAttr, setEditingCalcAttr] = React.useState<CalculatedAttribute | null>(null);

  const filteredTreeNodes = React.useMemo(
    () => filterTreeNodes(treeNodes, browserSearch),
    [treeNodes, browserSearch]
  );

  const openCalcDialog = (attr?: CalculatedAttribute) => {
    setEditingCalcAttr(attr || null);
    setCalcDialogOpen(true);
  };

  const closeCalcDialog = () => {
    setCalcDialogOpen(false);
    setEditingCalcAttr(null);
  };

  const displayNameMap = React.useMemo(() => buildDisplayNameMap(treeNodes), [treeNodes]);
  const getDisplayName = (name: string) => displayNameMap[name] || toDisplayName(name);

  const dateDimensions = semanticModel ? getDateDimensions(semanticModel) : [];

  const getMetricDetail = (name: string) => {
    if (!semanticModel) return null;
    return semanticModel.metrics.find((m) => m.name === name) || null;
  };

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
      {/* Left: Model Browser (toggleable) */}
      {isModelBrowserOpen ? (
        <Paper
          elevation={0}
          component="aside"
          aria-label="Model Browser"
          sx={qbBrowser}
        >
          <Box
            sx={qbBrowserHeader}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={qbBrowserTitle}>
              Model Browser
            </Typography>
            <Tooltip title="Collapse panel">
              <IconButton size="small" onClick={toggleModelBrowser} sx={qbIconButton}>
                <ChevronRightIcon sx={{ fontSize: 20, transform: 'rotate(180deg)' }} />
              </IconButton>
            </Tooltip>
          </Box>
          {treeNodes.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Select a domain and perspective to browse the semantic model.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 1.5, pt: 0.75, pb: 1, flexShrink: 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search metrics & dimensions..."
                  value={browserSearch}
                  onChange={(e) => setBrowserSearch(e.target.value)}
                  data-shortcut="search"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                    ...(browserSearch
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setBrowserSearch('')} sx={{ p: 0.25 }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }
                      : {}),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem' }, '& input': { py: 0.75 } }}
                />
              </Box>
              {filteredTreeNodes.length === 0 && browserSearch.trim() ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <SearchIcon sx={{ fontSize: 36, color: 'text.disabled', opacity: 0.3, mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    No results for "{browserSearch}"
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding sx={{ overflow: 'auto', flex: 1, px: 0.75, pb: 1.5 }}>
                  {filteredTreeNodes.map((node) => (
                    <TreeNodeItem key={node.id} node={node} />
                  ))}
                </List>
              )}
            </>
          )}
        </Paper>
      ) : (
        <Box
          sx={qbBrowserCollapsed}
          onClick={toggleModelBrowser}
        >
          <Tooltip title="Expand Model Browser" placement="right">
            <IconButton size="small" sx={{ ...qbIconButton, mb: 1 }}>
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              letterSpacing: '0.05em',
              fontSize: '0.7rem',
              fontWeight: 500,
              userSelect: 'none',
            }}
          >
            Model Browser
          </Typography>
        </Box>
      )}

      {/* Center: Selection Panes */}
      <Box sx={qbCards}>
        {/* Selected Metrics */}
        <SelectionCard testId="qb-card-metrics" title="Selected Metrics" icon={<BarChartIcon fontSize="small" color="primary" />}>
          {selectedMetrics.length === 0 && calculatedAttributes.filter((a) => a.type === 'metric').length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={qbEmpty}>
              No metrics selected. Use the tree view or agent to select metrics.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedMetrics.map((instance) => {
                const m = instance.metricName;
                const detail = getMetricDetail(m);
                const formula = detail?.formula || '';
                const config = metricConfigs[instance.id];
                const hasOverrides = !!(config?.aggregationOverride || config?.timePeriod || config?.alias);

                const timePeriodLabel = TIME_PERIOD_AGGREGATIONS.find(tp => tp.value === config?.timePeriod)?.label;
                const aggLabel = config?.aggregationOverride;

                const sortCol = getMetricPrimarySortColumn(m, config);
                const sortIdx = orderBy.findIndex((o) => o.column === sortCol);
                const sortDir = sortIdx >= 0 ? orderBy[sortIdx].direction : null;

                return (
                  <Chip
                    key={instance.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{getDisplayName(m)}</span>
                        {aggLabel && (
                          <Chip label={aggLabel} size="small" color="info" variant="outlined"
                            sx={qbBadge('info')} />
                        )}
                        {timePeriodLabel && (
                          <Chip label={timePeriodLabel} size="small" color="warning" variant="outlined"
                            sx={qbBadge('warn')} />
                        )}
                        {config?.alias && (
                          <Chip label={`"${config.alias}"`} size="small" color="default" variant="outlined"
                            sx={qbBadge('plain')} />
                        )}
                        {sortDir && (
                          <Tooltip title={`Sort ${sortDir}${orderBy.length > 1 ? ` (priority ${sortIdx + 1})` : ''} — click to remove`} arrow>
                            <Box
                              component="span"
                              onClick={(e) => { e.stopPropagation(); removeOrderBy(sortCol); }}
                              sx={{
                                display: 'inline-flex', alignItems: 'baseline', gap: 0.1,
                                color: 'success.main', fontWeight: 600, fontSize: '0.8rem',
                                cursor: 'pointer', lineHeight: 1,
                              }}
                            >
                              {sortDir === 'ASC' ? '↑' : '↓'}
                              {orderBy.length > 1 && (
                                <Box component="sup" sx={{ fontSize: '0.55rem', ml: 0.1 }}>{sortIdx + 1}</Box>
                              )}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    }
                    size="small"
                    color={hasOverrides ? 'primary' : 'primary'}
                    variant="outlined"
                    onDelete={() => removeMetricInstance(instance.id)}
                    sx={qbMetricChip}
                    data-testid="qb-metric-chip"
                    icon={
                      <MetricConfigPopover
                        metricName={m}
                        instanceId={instance.id}
                        formula={formula}
                        dateDimensions={dateDimensions}
                      />
                    }
                  />
                );
              })}
              {calculatedAttributes.filter((a) => a.type === 'metric').map((attr) => {
                const sortIdx = orderBy.findIndex((o) => o.column === attr.name);
                const sortDir = sortIdx >= 0 ? orderBy[sortIdx].direction : null;
                return (
                  <Tooltip key={attr.id} title={`Formula: ${attr.formula} — click icon to edit; click sort badge to cycle`} placement="top" arrow>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FunctionsIcon sx={{ fontSize: 14, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openCalcDialog(attr); }} />
                          <span>{attr.name}</span>
                          <Box
                            component="span"
                            onClick={(e) => { e.stopPropagation(); cycleOrderBy(attr.name); }}
                            sx={{
                              display: 'inline-flex', alignItems: 'baseline', gap: 0.1,
                              color: sortDir ? 'success.main' : 'text.disabled',
                              fontWeight: sortDir ? 600 : 400,
                              fontSize: '0.8rem', lineHeight: 1, cursor: 'pointer',
                              px: 0.25,
                            }}
                          >
                            {sortDir ? (sortDir === 'ASC' ? '↑' : '↓') : '⇅'}
                            {sortDir && orderBy.length > 1 && (
                              <Box component="sup" sx={{ fontSize: '0.55rem', ml: 0.1 }}>{sortIdx + 1}</Box>
                            )}
                          </Box>
                        </Box>
                      }
                      size="small"
                      color="info"
                      variant="outlined"
                      onDelete={() => removeCalculatedField(attr.id)}
                      sx={{ ...qbCalcChip, cursor: 'default' }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </SelectionCard>

        {/* Selected Dimensions */}
        <SelectionCard testId="qb-card-dimensions" title="Selected Dimensions" icon={<ViewColumnIcon fontSize="small" color="secondary" />}>
          {selectedDimensions.length === 0 && calculatedAttributes.filter((a) => a.type === 'dimension').length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={qbEmpty}>
              No dimensions selected.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedDimensions.map((d) => {
                const alias = getDimensionOutputAlias(d);
                const sortIdx = orderBy.findIndex((o) => o.column === alias);
                const sortDir = sortIdx >= 0 ? orderBy[sortIdx].direction : null;
                const tipBase = sortDir
                  ? `Sorted ${sortDir} (priority ${sortIdx + 1}). Click to cycle → `
                  : 'Click to sort → ASC → DESC → none';
                const nextLabel = !sortDir ? 'ASC' : sortDir === 'ASC' ? 'DESC' : 'none';
                return (
                  <Tooltip key={d} title={sortDir ? `${tipBase}${nextLabel}` : tipBase} arrow>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{getDisplayName(d)}</span>
                          {sortDir && (
                            <Box component="span" sx={{
                              display: 'inline-flex', alignItems: 'baseline', gap: 0.1,
                              color: 'success.main', fontWeight: 600, fontSize: '0.8rem', lineHeight: 1,
                            }}>
                              {sortDir === 'ASC' ? '↑' : '↓'}
                              {orderBy.length > 1 && (
                                <Box component="sup" sx={{ fontSize: '0.55rem', ml: 0.1 }}>{sortIdx + 1}</Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                      size="small"
                      color="secondary"
                      variant="outlined"
                      onClick={() => cycleOrderBy(alias)}
                      onDelete={() => toggleDimension(d)}
                      sx={qbDimensionChip}
                      data-testid="qb-dimension-chip"
                    />
                  </Tooltip>
                );
              })}
              {calculatedAttributes.filter((a) => a.type === 'dimension').map((attr) => {
                const sortIdx = orderBy.findIndex((o) => o.column === attr.name);
                const sortDir = sortIdx >= 0 ? orderBy[sortIdx].direction : null;
                return (
                  <Tooltip key={attr.id} title={`Formula: ${attr.formula} — click icon to edit; click sort badge to cycle`} placement="top" arrow>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FunctionsIcon sx={{ fontSize: 14, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openCalcDialog(attr); }} />
                          <span>{attr.name}</span>
                          <Box
                            component="span"
                            onClick={(e) => { e.stopPropagation(); cycleOrderBy(attr.name); }}
                            sx={{
                              display: 'inline-flex', alignItems: 'baseline', gap: 0.1,
                              color: sortDir ? 'success.main' : 'text.disabled',
                              fontWeight: sortDir ? 600 : 400,
                              fontSize: '0.8rem', lineHeight: 1, cursor: 'pointer',
                              px: 0.25,
                            }}
                          >
                            {sortDir ? (sortDir === 'ASC' ? '↑' : '↓') : '⇅'}
                            {sortDir && orderBy.length > 1 && (
                              <Box component="sup" sx={{ fontSize: '0.55rem', ml: 0.1 }}>{sortIdx + 1}</Box>
                            )}
                          </Box>
                        </Box>
                      }
                      size="small"
                      color="info"
                      variant="outlined"
                      onDelete={() => removeCalculatedField(attr.id)}
                      sx={{ ...qbCalcChip, cursor: 'default' }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </SelectionCard>

        {/* Filters */}
        <SelectionCard testId="qb-card-filters" title="Filters" icon={<FilterListIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
            <CalendarMonthIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontSize: 12 }}>Date Range</InputLabel>
              <Select
                value={dateRangePreset}
                label="Date Range"
                onChange={(e) => setDateRangePreset(e.target.value as DateRangePreset)}
                sx={{ fontSize: 12 }}
              >
                {DATE_RANGE_PRESETS.map((p) => (
                  <MenuItem key={p.key} value={p.key} sx={{ fontSize: 12 }}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="From"
              type="date"
              value={dateRangeStart || ''}
              onChange={(e) => setDateRange(e.target.value || null, dateRangeEnd)}
              disabled={dateRangePreset !== 'CUSTOM'}
              sx={{ minWidth: 148, '& input': { fontSize: 12 } }}
              InputLabelProps={{ shrink: true, sx: { fontSize: 12 } }}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              value={dateRangeEnd || ''}
              onChange={(e) => setDateRange(dateRangeStart, e.target.value || null)}
              disabled={dateRangePreset !== 'CUSTOM'}
              sx={{ minWidth: 148, '& input': { fontSize: 12 } }}
              InputLabelProps={{ shrink: true, sx: { fontSize: 12 } }}
            />
          </Box>
          <FilterGroupBuilder
            group={filterGroup}
            semanticModel={semanticModel}
            getDisplayName={getDisplayName}
            isRoot
          />
        </SelectionCard>

        {/* Calculated Attributes */}
        <SelectionCard testId="qb-card-calculated" title="Calculated Attributes" icon={<FunctionsIcon fontSize="small" />}>
          {calculatedAttributes.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {calculatedAttributes.map((attr) => (
                <Tooltip key={attr.id} title={`Click to edit | ${attr.type}: ${attr.formula}`} placement="top" arrow>
                  <Chip
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{attr.name}</span>
                        <Chip
                          label={attr.type}
                          size="small"
                          color={attr.type === 'metric' ? 'primary' : 'secondary'}
                          variant="outlined"
                          sx={qbBadge(attr.type === 'metric' ? 'metric' : 'plain')}
                        />
                      </Box>
                    }
                    size="small"
                    color="info"
                    variant="outlined"
                    onClick={() => openCalcDialog(attr)}
                    onDelete={() => removeCalculatedField(attr.id)}
                    sx={{ ...qbCalcChip, cursor: 'pointer' }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openCalcDialog()}
          >
            Add Calculated Field
          </Button>
          <Dialog
            open={calcDialogOpen}
            onClose={closeCalcDialog}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>{editingCalcAttr ? 'Edit Calculated Field' : 'Calculated Field Builder'}</DialogTitle>
            <DialogContent>
              <CalculatedComponents onClose={closeCalcDialog} editingAttribute={editingCalcAttr} />
            </DialogContent>
          </Dialog>
        </SelectionCard>

      </Box>
    </Box>
  );
};

export default VoyagerPanes;
